import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/landing/Reveal";
import { categories } from "@/lib/landingData";
import { Plus, ArrowUpRight } from "lucide-react";

const PointList = ({ points }) => (
  <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
    {points.map((p) => (
      <li key={p} className="flex items-center gap-2 text-sm text-white/80">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-brand" />
        {p}
      </li>
    ))}
  </ul>
);

/* ---------------- Desktop: expanding horizontal panels ---------------- */
const DesktopPanels = ({ active, setActive }) => (
  <div className="hidden gap-3 lg:flex lg:h-[480px]">
    {categories.map((cat, i) => {
      const Icon = cat.icon;
      const isActive = active === i;
      return (
        <motion.div
          key={cat.key}
          data-testid={`category-panel-${cat.key}`}
          onMouseEnter={() => setActive(i)}
          onClick={() => setActive(i)}
          animate={{ flexGrow: isActive ? 7 : 1 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className={`group relative min-w-[72px] cursor-pointer overflow-hidden rounded-2xl border transition-colors duration-300 ${
            isActive ? "border-white/25" : "border-white/10 hover:border-white/20"
          }`}
          style={{ flexBasis: 0 }}
        >
          {/* background */}
          {cat.video ? (
            isActive ? (
              <>
                <video
                  src={cat.video}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  className="absolute inset-0 h-full w-full scale-105 object-cover opacity-90 transition-opacity duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/55 to-transparent" />
              </>
            ) : (
              <div className="absolute inset-0 bg-[#0A0A0C]" />
            )
          ) : cat.image ? (
            <>
              <img
                src={cat.image}
                alt=""
                className={`absolute inset-0 h-full w-full object-cover transition-all duration-700 ${
                  isActive ? "scale-105 opacity-40" : "opacity-20"
                }`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/85 to-[#050505]/40" />
            </>
          ) : (
            <div className="absolute inset-0 bg-[#0A0A0C]" />
          )}
          <div
            className={`pointer-events-none absolute -bottom-24 left-1/2 h-64 w-64 -translate-x-1/2 glow-orange blur-3xl transition-opacity duration-500 ${
              isActive ? "opacity-70" : "opacity-0"
            }`}
          />

          {/* number */}
          <span className="absolute right-4 top-4 z-10 font-mega text-sm text-white/40">
            {i + 1 < 10 ? `0${i + 1}` : i + 1}
          </span>

          {/* Collapsed label */}
          <AnimatePresence>
            {!isActive && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="absolute inset-0 z-10 flex flex-col items-center justify-between py-6"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/12 bg-white/[0.04] text-white">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="font-display text-lg font-bold uppercase tracking-wide text-white/70 [writing-mode:vertical-rl] rotate-180">
                  {cat.label}
                </span>
                <Plus className="h-5 w-5 text-white/40" />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Expanded content */}
          <AnimatePresence>
            {isActive && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, delay: 0.12 }}
                className="absolute inset-0 z-10 flex flex-col justify-end p-8"
              >
                <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-brand text-black">
                  <Icon className="h-6 w-6" />
                </span>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gradient">
                  {cat.label}
                </p>
                <h3 className="mt-2 font-mega text-3xl leading-none text-white">
                  {cat.goal}
                </h3>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-muted-foreground">
                  {cat.copy}
                </p>
                <PointList points={cat.points} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      );
    })}
  </div>
);

/* ---------------- Mobile: animated accordion ---------------- */
const MobileAccordion = ({ active, setActive }) => (
  <div className="flex flex-col gap-3 lg:hidden">
    {categories.map((cat, i) => {
      const Icon = cat.icon;
      const isActive = active === i;
      return (
        <div
          key={cat.key}
          data-testid={`category-acc-${cat.key}`}
          className={`relative overflow-hidden rounded-2xl border transition-colors ${
            isActive ? "border-white/25 bg-[#0C0C0E]" : "border-white/10 bg-[#0A0A0C]"
          }`}
        >
          {isActive && cat.video && (
            <>
              <video
                src={cat.video}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="absolute inset-0 h-full w-full object-cover opacity-70"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/30" />
            </>
          )}
          <button
            onClick={() => setActive(isActive ? -1 : i)}
            className="relative z-10 flex w-full items-center gap-4 p-5 text-left"
          >
            <span
              className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors ${
                isActive ? "bg-gradient-brand text-black" : "border border-white/12 bg-white/[0.04] text-white"
              }`}
            >
              <Icon className="h-5 w-5" />
            </span>
            <span className="flex-1">
              <span className="block font-display text-lg font-bold uppercase tracking-tight text-white">
                {cat.label}
              </span>
              <span className="block text-sm text-muted-foreground">{cat.goal}</span>
            </span>
            <motion.span animate={{ rotate: isActive ? 45 : 0 }} transition={{ duration: 0.3 }}>
              <Plus className="h-5 w-5 text-white/60" />
            </motion.span>
          </button>
          <AnimatePresence initial={false}>
            {isActive && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 overflow-hidden"
              >
                <div className="px-5 pb-6">
                  <p className="text-sm leading-relaxed text-muted-foreground">{cat.copy}</p>
                  <PointList points={cat.points} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    })}
  </div>
);

export const Categories = () => {
  const [active, setActive] = useState(2);

  return (
    <section data-testid="categories" className="relative py-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="mx-auto mb-16 max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gradient">
            One AI. Every Goal.
          </p>
          <h2 className="mt-5 font-mega text-4xl leading-[0.95] text-white sm:text-5xl lg:text-6xl">
            Whatever Your Dream,
            <br />
            b&rarr;come Gets You There
          </h2>
          <p className="mx-auto mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <ArrowUpRight className="h-4 w-4 text-gold" />
            Hover or tap a category to explore
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <DesktopPanels active={active} setActive={setActive} />
          <MobileAccordion active={active} setActive={setActive} />
        </Reveal>
      </div>
    </section>
  );
};

export default Categories;
